import React, { useState, useEffect, useCallback } from "react";
import {
  Table, Button, Input, Select, Tag, Modal, Upload, Space,
  Row, Col, Card, Typography, Tooltip, Badge, message, Empty,
  Progress, Alert, Divider, Tabs,
} from "antd";
import {
  SearchOutlined, EyeOutlined, UploadOutlined, FilePdfOutlined,
  ReloadOutlined, CheckCircleFilled, ExclamationCircleFilled,
  InboxOutlined, DownloadOutlined, CheckSquareOutlined,
  CloseSquareOutlined, FolderOutlined, FileTextOutlined,
  LinkOutlined, WarningOutlined,
} from "@ant-design/icons";
import PreviewActa from "./PreviewActa";
import useDescargaActasGeneradas from "./Usedescargaactasgeneradas ";
import useDescargaActasFirmadas from "./Usedescargaactasfirmadas";
import { useSelector } from 'react-redux';
import { getDescargarActaParticipante, getParticipantesActa, subirActaParticipante } from "../../services/participantes/participantesActaServices";



const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

// ─── Modal cargar documento ───────────────────────────────────────────────────
const CargarDocumentoModal = ({ open, participante, onClose, onSuccess, username }) => {
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList]   = useState([]);

const handleUpload = async () => {
    if (!fileList.length || !participante) {
      message.warning("Selecciona un archivo PDF antes de cargar.");
      return;
    }
    setUploading(true);
    try {
      const archivoOriginal = fileList[0].originFileObj ?? fileList[0]; // 👈 fallback por si acaso

      const nuevoNombre = `acta_${participante.id_registro}_${participante.num_identificacion}.pdf`;
      // ✅ Leer como ArrayBuffer antes de renombrar
      const buffer = await archivoOriginal.arrayBuffer();
      const archivoRenombrado = new File(
        [buffer],          // 👈 usa el buffer directo, no el File original
        nuevoNombre,
        { type: 'application/pdf' }
      );

      await subirActaParticipante(
        participante.id,
        archivoRenombrado,
        username,
        participante.departamento,
        participante.municipio
      );

      message.success(`Documento cargado para ${participante.nombre_completo}.`);
      onSuccess(participante.id);
      setFileList([]);
      onClose();
    } catch (error) {
      const msg = error?.response?.data?.mensaje ?? "Error al cargar. Intenta nuevamente.";
      message.error(msg);
    } finally {
      setUploading(false);
    }
  };


  const uploadProps = {
  accept: '.pdf',
  maxCount: 1,
  fileList,
  beforeUpload: (file) => {
    const isPDF = file.type === 'application/pdf';
    const isLt10M = file.size / 1024 / 1024 < 10;

    if (!isPDF) {
      message.error('Solo se permiten archivos PDF.');
      return Upload.LIST_IGNORE;
    }
    if (!isLt10M) {
      message.error('El archivo no debe superar los 10 MB.');
      return Upload.LIST_IGNORE;
    }

    // ✅ Estructura completa que Ant Design necesita para mostrar el archivo
    const fileItem = {
      uid: file.uid ?? `-${Date.now()}`,
      name: file.name,                 // 👈 nombre que se muestra en la lista
      status: 'done',                  // 👈 necesario para que lo muestre sin spinner
      size: file.size,
      type: file.type,
      originFileObj: file,             // 👈 archivo real para subir
    };

    setFileList([fileItem]);
    return false;
  },
  onRemove: () => setFileList([]),
};

  if (!participante) return null;
  return (
    <Modal
  open={open}
  onCancel={() => { if (!uploading) { setFileList([]); onClose(); } }}
  title={<Space><UploadOutlined style={{ color: "#52c41a" }} /><span>Cargar Documento</span></Space>}
  footer={
    <Space>
      <Button onClick={() => { setFileList([]); onClose(); }} disabled={uploading}>Cancelar</Button>
      <Button type="primary" icon={<UploadOutlined />} onClick={handleUpload} loading={uploading} disabled={!fileList.length}>
        {uploading ? "Cargando..." : "Cargar documento"}
      </Button>
    </Space>
  }
  width={560}
>
  <Card size="small" style={{ marginBottom: 20, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
    <Space direction="vertical" size={2}>
      <Text strong>{participante.nombre_completo}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {participante.tipo_documento} · {participante.num_identificacion} · {participante.municipio}, {participante.departamento}
      </Text>
    </Space>
  </Card>
  <Dragger {...uploadProps} style={{ borderRadius: 8 }}>
    <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: "#1677ff", fontSize: 40 }} /></p>
    <p className="ant-upload-text" style={{ fontSize: 15, fontWeight: 600 }}>Arrastra el PDF aquí o haz clic para seleccionar</p>
    <p className="ant-upload-hint" style={{ color: "#8c8c8c" }}>Solo <strong>.pdf</strong> · Máximo <strong>10 MB</strong></p>
  </Dragger>
</Modal>
  );
};

// ─── Modal progreso genérico ──────────────────────────────────────────────────
const ModalProgreso = ({ open, titulo, icono, color, zipNombre, progreso, actual, total, nombreActual }) => (
  <Modal open={open} closable={false} maskClosable={false} footer={null} width={480}
    title={<Space>{icono}<span>{titulo}</span></Space>}
  >
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Alert type="info" showIcon icon={<FolderOutlined />}
        message={`Generando ${zipNombre}`}
        description="Por favor espera. No cierres esta ventana."
      />
      <div>
        <Row justify="space-between" style={{ marginBottom: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Procesando {actual} de {total}</Text>
          <Text strong style={{ fontSize: 12 }}>{progreso}%</Text>
        </Row>
        <Progress percent={progreso} status={progreso === 100 ? "success" : "active"}
          strokeColor={{ from: color, to: "#52c41a" }} />
      </div>
      {nombreActual && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          <FilePdfOutlined style={{ marginRight: 4 }} />{nombreActual}
        </Text>
      )}
    </Space>
  </Modal>
);

// ─── Modal errores de descarga firmadas ──────────────────────────────────────
const ModalErrores = ({ open, errores, onClose }) => (
  <Modal open={open} onCancel={onClose} footer={<Button onClick={onClose}>Cerrar</Button>}
    title={<Space><WarningOutlined style={{ color: "#faad14" }} /><span>Archivos con error</span></Space>}
    width={520}
  >
    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
      message={`${errores.length} archivo${errores.length > 1 ? "s" : ""} no se pudo${errores.length > 1 ? "ron" : ""} descargar`}
      description="Los demás archivos se incluyeron correctamente en el ZIP."
    />
    {errores.map((e, i) => (
      <Card key={i} size="small" style={{ marginBottom: 8, background: "#fff7e6", border: "1px solid #ffd591" }}>
        <Text strong>{e.nombre}</Text>
        <br />
        <Text type="secondary" style={{ fontSize: 11 }}>CC: {e.cedula} · Error: {e.error}</Text>
      </Card>
    ))}
  </Modal>
);

// ─── Barra de acciones masivas ────────────────────────────────────────────────
const BarraAccionesMasivas = ({
  tipo, // "generadas" | "firmadas"
  selectedKeys, total, onSeleccionarTodos, onLimpiar, onDescargar, descargando,
}) => {
  const esGeneradas = tipo === "generadas";
  const color       = esGeneradas ? "#1677ff" : "#52c41a";
  const bgColor     = esGeneradas ? "#e6f4ff"  : "#f6ffed";
  const border      = esGeneradas ? "#91caff"  : "#b7eb8f";
  const zipNombre   = esGeneradas ? "actas_generadas.zip" : "actas_firmadas.zip";
  const icono       = esGeneradas ? <FileTextOutlined style={{ color, fontSize: 18 }} /> : <LinkOutlined style={{ color, fontSize: 18 }} />;

  if (!selectedKeys.length) return null;

  return (
    <Card
      style={{ marginBottom: 12, borderRadius: 8, border: `1px solid ${border}`, background: bgColor }}
      bodyStyle={{ padding: "12px 20px" }}
    >
      <Row align="middle" justify="space-between" wrap={false}>
        <Col>
          <Space size={12} align="center">
            {icono}
            <Text strong style={{ color }}>
              {selectedKeys.length} participante{selectedKeys.length > 1 ? "s" : ""} seleccionado{selectedKeys.length > 1 ? "s" : ""}
            </Text>
            <Divider type="vertical" />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Se descargará <Text code style={{ fontSize: 11 }}>{zipNombre}</Text>
            </Text>
          </Space>
        </Col>
        <Col>
          <Space>
            <Tooltip title={`Seleccionar todos (${total})`}>
              <Button size="small" icon={<CheckSquareOutlined />} onClick={onSeleccionarTodos}
                disabled={selectedKeys.length === total}>
                Todos ({total})
              </Button>
            </Tooltip>
            <Tooltip title="Limpiar selección">
              <Button size="small" icon={<CloseSquareOutlined />} onClick={onLimpiar} />
            </Tooltip>
            <Button
              type="primary"
              style={esGeneradas ? {} : { background: "#52c41a", borderColor: "#52c41a" }}
              icon={<DownloadOutlined />}
              onClick={onDescargar}
              loading={descargando}
              disabled={descargando}
            >
              {descargando ? "Generando..." : `Descargar ${selectedKeys.length} acta${selectedKeys.length > 1 ? "s" : ""}`}
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
const ParticipantesActa = () => {
  const [data, setData]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ nombre: "", departamento: "", municipio: "", tipoDocumento: "" });
  const [previewParticipante, setPreviewParticipante] = useState(null);
  const [uploadModal, setUploadModal] = useState({ open: false, participante: null });
  const [modalErrores, setModalErrores] = useState(false);

  // Selecciones INDEPENDIENTES por tipo
  const [selGeneradas, setSelGeneradas] = useState([]); // SIN_DOCUMENTO
  const [selFirmadas,  setSelFirmadas]  = useState([]); // DOCUMENTO_CARGADO

  // Hooks de descarga separados
  const hookGeneradas = useDescargaActasGeneradas();
  const hookFirmadas  = useDescargaActasFirmadas();

    const usernameFromState = useSelector((state) => state.auth.user);
  

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getParticipantesActa(usernameFromState.cod_municipio);
      if (!res.status) throw new Error();
      setData(res.data);
    } catch {
      message.error("Sin conexión. Mostrando datos de ejemplo.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUploadSuccess = (id) =>
    setData((prev) => prev.map((p) => p.id === id ? { ...p, estado_pdf: "DOCUMENTO_CARGADO" } : p));

  // ── Filtrado base (sin filtrar por estado) ─────────────────────────────────
  const filteredBase = data.filter((p) => {
    const matchNombre = !filters.nombre       || p.nombre_completo.toLowerCase().includes(filters.nombre.toLowerCase()) || p.num_identificacion.includes(filters.nombre);
    const matchDpto   = !filters.departamento || p.departamento === filters.departamento;
    const matchMun    = !filters.municipio    || p.municipio === filters.municipio;
    const matchTipo   = !filters.tipoDocumento|| p.tipo_documento === filters.tipoDocumento;
    return matchNombre && matchDpto && matchMun && matchTipo;
  });

  // Separados por estado
  const sinDocumento  = filteredBase.filter((p) => p.estado_pdf === "SIN_DOCUMENTO");
  const conDocumento  = filteredBase.filter((p) => p.estado_pdf === "DOCUMENTO_CARGADO");

  const departamentos = [...new Set(data.map((p) => p.departamento))].sort();
  const municipios    = [...new Set(data.filter((p) => !filters.departamento || p.departamento === filters.departamento).map((p) => p.municipio))].sort();
  const tiposDoc      = [...new Set(data.map((p) => p.tipo_documento))].sort();

  // ── Handlers descarga ──────────────────────────────────────────────────────
  const handleDescargarGeneradas = async () => {
    const participantes = data.filter((p) => selGeneradas.includes(p.id));
    await hookGeneradas.descargar(participantes);
    message.success(`actas_generadas.zip descargado con ${participantes.length} acta${participantes.length > 1 ? "s" : ""}.`);
    setSelGeneradas([]);
  };

  const handleDescargarFirmadas = async () => {
    const participantes = data.filter((p) => selFirmadas.includes(p.id));
    await hookFirmadas.descargar(participantes);
    const ok = participantes.length - hookFirmadas.errores.length;
    if (hookFirmadas.errores.length > 0) {
      message.warning(`Descarga completada: ${ok} OK, ${hookFirmadas.errores.length} con error.`);
      setModalErrores(true);
    } else {
      message.success(`actas_firmadas.zip descargado con ${ok} acta${ok > 1 ? "s" : ""}.`);
    }
    setSelFirmadas([]);
  };


  const handleDescargarActa = async (record) => {
  try {
    message.loading({ content: 'Descargando documento...', key: 'descarga' });
    
    await getDescargarActaParticipante(
      record.id,
      `acta_${record.id_registro}_${record.num_identificacion}.pdf`
    );

    message.success({ content: 'Documento descargado correctamente.', key: 'descarga', duration: 3 });
  } catch (error) {
    message.error({ content: 'Error al descargar el documento.', key: 'descarga', duration: 3 });
  }
};



  // ── Columnas compartidas ───────────────────────────────────────────────────
  const colsBase = [
    { title: "Registro",          dataIndex: "id_registro",       key: "id_registro",       width: 110, render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    { title: "Tipo Doc.",         dataIndex: "tipo_documento",     key: "tipo_documento",     width: 85,  align: "center", render: (v) => <Tag color="blue">{v}</Tag> },
    { title: "N° Identificación", dataIndex: "num_identificacion", key: "num_identificacion", width: 155 },
    { title: "Nombre Completo",   dataIndex: "nombre_completo",    key: "nombre_completo",    ellipsis: true, render: (v) => <Text strong>{v}</Text> },
    { title: "Municipio",         dataIndex: "municipio",          key: "municipio",          width: 120, ellipsis: true },
    { title: "Departamento",      dataIndex: "departamento",       key: "departamento",       width: 145, ellipsis: true },
  ];

  // Columna acciones SIN_DOCUMENTO
  const colsSinDoc = [
    ...colsBase,
    {
      title: "Acciones", key: "acciones", fixed: "right", width: 200, align: "center",
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="Vista previa del acta">
            <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewParticipante(record)}>
              Preview
            </Button>
          </Tooltip>
          <Tooltip title="Cargar documento PDF firmado">
            <Button size="small" type="primary" icon={<UploadOutlined />}
              onClick={() => setUploadModal({ open: true, participante: record })}>
              Cargar
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Columna acciones DOCUMENTO_CARGADO
  const colsConDoc = [
    ...colsBase,
    {
      title: "Acciones", key: "acciones", fixed: "right", width: 155, align: "center",
      render: (_, record) => (
        <Tooltip title="Abrir PDF firmado">
         <Button
            size="small"
            type="primary"
            ghost
            icon={<FilePdfOutlined />}
            onClick={() => handleDescargarActa(record)}
        >
            Ver documento
         </Button>
        </Tooltip>
      ),
    },
  ];

  // ── Preview pantalla completa ──────────────────────────────────────────────
  if (previewParticipante) {
    return <PreviewActa participante={previewParticipante} onVolver={() => setPreviewParticipante(null)} />;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
  <Card
            style={{ margin: 20 }}
        >
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0 }}>Participantes del Actaa</Title>
        <Text type="secondary">Gestión de documentos por participante verificado</Text>
        <Space style={{ marginTop: 12 }} size={12}>
          <Badge count={data.length} showZero color="#1677ff">
            <Tag style={{ padding: "4px 12px", fontSize: 13 }}>Total</Tag>
          </Badge>
          <Badge count={conDocumento.length} showZero color="#52c41a">
            <Tag color="success" style={{ padding: "4px 12px", fontSize: 13 }}>Actas firmadas</Tag>
          </Badge>
          <Badge count={sinDocumento.length} showZero color="#faad14">
            <Tag color="warning" style={{ padding: "4px 12px", fontSize: 13 }}>Sin documento</Tag>
          </Badge>
        </Space>
      </div>

      {/* Filtros comunes */}
      <Card style={{ marginBottom: 16, borderRadius: 8 }} bodyStyle={{ paddingBottom: 8 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input placeholder="Buscar nombre o identificación" prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filters.nombre} onChange={(e) => setFilters((f) => ({ ...f, nombre: e.target.value }))} allowClear />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Select placeholder="Tipo documento" value={filters.tipoDocumento || undefined}
              onChange={(v) => setFilters((f) => ({ ...f, tipoDocumento: v ?? "" }))} allowClear style={{ width: "100%" }}>
              {tiposDoc.map((t) => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Select placeholder="Departamento" value={filters.departamento || undefined}
              onChange={(v) => setFilters((f) => ({ ...f, departamento: v ?? "", municipio: "" }))}
              allowClear style={{ width: "100%" }} showSearch optionFilterProp="children">
              {departamentos.map((d) => <Option key={d} value={d}>{d}</Option>)}
            </Select>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <Select placeholder="Municipio" value={filters.municipio || undefined}
              onChange={(v) => setFilters((f) => ({ ...f, municipio: v ?? "" }))}
              allowClear style={{ width: "100%" }} showSearch optionFilterProp="children">
              {municipios.map((m) => <Option key={m} value={m}>{m}</Option>)}
            </Select>
          </Col>
          <Col xs={24} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Space>
              <Button onClick={() => setFilters({ nombre: "", departamento: "", municipio: "", tipoDocumento: "" })}>
                Limpiar filtros
              </Button>
              <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Actualizar</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabs con las dos tablas separadas */}
      <Tabs
        defaultActiveKey="sin_documento"
        type="card"
        items={[
          {
            key: "sin_documento",
            label: (
              <Space>
                <ExclamationCircleFilled style={{ color: "#faad14" }} />
                <span>Sin Documento</span>
                <Badge count={sinDocumento.length} showZero color="#faad14" />
              </Space>
            ),
            children: (
              <>
                {/* Barra descarga actas generadas */}
                <BarraAccionesMasivas
                  tipo="generadas"
                  selectedKeys={selGeneradas}
                  total={sinDocumento.length}
                  onSeleccionarTodos={() => setSelGeneradas(sinDocumento.map((p) => p.id))}
                  onLimpiar={() => setSelGeneradas([])}
                  onDescargar={handleDescargarGeneradas}
                  descargando={hookGeneradas.descargando}
                />

                <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
                  {selGeneradas.length === 0 && (
                    <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <FileTextOutlined style={{ marginRight: 6, color: "#faad14" }} />
                        Selecciona participantes para descargar sus actas generadas en <Text code style={{ fontSize: 11 }}>actas_generadas.zip</Text>
                      </Text>
                      <Button size="small" icon={<CheckSquareOutlined />}
                        onClick={() => setSelGeneradas(sinDocumento.map((p) => p.id))}
                        disabled={!sinDocumento.length}>
                        Seleccionar todos
                      </Button>
                    </div>
                  )}
                  <Table
                    rowSelection={{
                      selectedRowKeys: selGeneradas,
                      onChange: setSelGeneradas,
                      selections: [
                        { key: "all", text: `Seleccionar todos (${sinDocumento.length})`, onSelect: () => setSelGeneradas(sinDocumento.map((p) => p.id)) },
                        { key: "none", text: "Limpiar selección", onSelect: () => setSelGeneradas([]) },
                      ],
                    }}
                    columns={colsSinDoc}
                    dataSource={sinDocumento}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 900 }}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "25", "50"], showTotal: (t, r) => `${r[0]}–${r[1]} de ${t}` }}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay participantes sin documento" /> }}
                    rowClassName={() => "row-sin-documento"}
                  />
                </Card>
              </>
            ),
          },
          {
            key: "documento_cargado",
            label: (
              <Space>
                <CheckCircleFilled style={{ color: "#52c41a" }} />
                <span>Actas Firmadas</span>
                <Badge count={conDocumento.length} showZero color="#52c41a" />
              </Space>
            ),
            children: (
              <>
                {/* Barra descarga actas firmadas */}
                <BarraAccionesMasivas
                  tipo="firmadas"
                  selectedKeys={selFirmadas}
                  total={conDocumento.length}
                  onSeleccionarTodos={() => setSelFirmadas(conDocumento.map((p) => p.id))}
                  onLimpiar={() => setSelFirmadas([])}
                  onDescargar={handleDescargarFirmadas}
                  descargando={hookFirmadas.descargando}
                />

                <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
                  {selFirmadas.length === 0 && (
                    <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <LinkOutlined style={{ marginRight: 6, color: "#52c41a" }} />
                        Selecciona participantes para descargar sus actas firmadas en <Text code style={{ fontSize: 11 }}>actas_firmadas.zip</Text>
                      </Text>
                      <Button size="small" icon={<CheckSquareOutlined />}
                        onClick={() => setSelFirmadas(conDocumento.map((p) => p.id))}
                        disabled={!conDocumento.length}>
                        Seleccionar todos
                      </Button>
                    </div>
                  )}
                  <Table
                    rowSelection={{
                      selectedRowKeys: selFirmadas,
                      onChange: setSelFirmadas,
                      selections: [
                        { key: "all", text: `Seleccionar todos (${conDocumento.length})`, onSelect: () => setSelFirmadas(conDocumento.map((p) => p.id)) },
                        { key: "none", text: "Limpiar selección", onSelect: () => setSelFirmadas([]) },
                      ],
                    }}
                    columns={colsConDoc}
                    dataSource={conDocumento}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 900 }}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "25", "50"], showTotal: (t, r) => `${r[0]}–${r[1]} de ${t}` }}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay actas firmadas" /> }}
                  />
                </Card>
              </>
            ),
          },
        ]}
      />

      {/* Modales de progreso independientes */}
      <ModalProgreso
        open={hookGeneradas.descargando}
        titulo="Generando actas..."
        icono={<FileTextOutlined style={{ color: "#1677ff" }} />}
        color="#1677ff"
        zipNombre="actas_generadas.zip"
        progreso={hookGeneradas.progreso}
        actual={hookGeneradas.actual}
        total={hookGeneradas.total}
        nombreActual={hookGeneradas.nombreActual}
      />

      <ModalProgreso
        open={hookFirmadas.descargando}
        titulo="Descargando actas firmadas..."
        icono={<LinkOutlined style={{ color: "#52c41a" }} />}
        color="#52c41a"
        zipNombre="actas_firmadas.zip"
        progreso={hookFirmadas.progreso}
        actual={hookFirmadas.actual}
        total={hookFirmadas.total}
        nombreActual={hookFirmadas.nombreActual}
      />

      {/* Modal errores en firmadas */}
      <ModalErrores
        open={modalErrores}
        errores={hookFirmadas.errores}
        onClose={() => setModalErrores(false)}
      />

      {/* Modal cargar documento */}
      <CargarDocumentoModal
        open={uploadModal.open}
        participante={uploadModal.participante}
        onClose={() => setUploadModal({ open: false, participante: null })}
        onSuccess={handleUploadSuccess}
        username={usernameFromState.username}
      />

      <style>{`
        .row-sin-documento { background-color: #fffbe6; }
        .row-sin-documento:hover > td { background-color: #fff7cc !important; }
      `}</style>
 </Card>
  );
};

export default ParticipantesActa;